import { formatEUR, type SavingsGoalResponse } from '@budget/shared';
import { type RefObject, useState } from 'react';
import { ApiError } from '../api/client';
import { useSavings, useWithdrawFromSavings } from '../api/hooks';
import { AmountField } from './AmountField';
import { BottomSheet } from './BottomSheet';

interface Props {
  /** Compte à renflouer — celui que l'écran affichait au moment de l'alerte. */
  accountId: string;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

/**
 * Renflouement du compte courant, au choix parmi TOUS les pôles.
 *
 * Distincte de `SavingsMoveModal`, qui vise un pôle déjà choisi : ici on part
 * de l'urgence — le compte est à sec — et il faut d'abord décider où puiser.
 *
 * Chaque virement est une vraie écriture (`transfer`, rattachée au pôle) :
 * elle apparaît dans la liste des écritures comme n'importe quel mouvement
 * entre comptes, pas comme un ajustement caché.
 */
export function SavingsWithdrawModal({ accountId, anchorRef, onClose }: Props) {
  const savings = useSavings();
  const withdraw = useWithdrawFromSavings();

  const [target, setTarget] = useState<SavingsGoalResponse | null>(null);
  const [amountCents, setAmountCents] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ name: string; amountCents: number } | null>(null);

  // Un pôle vide n'a rien à donner : autant ne pas le proposer.
  const goals = (savings.data?.goals ?? []).filter((goal) => goal.savedCents > 0);

  function openFor(goal: SavingsGoalResponse) {
    setTarget(goal);
    setAmountCents(goal.savedCents);
    setError(null);
    setDone(null);
  }

  async function submit() {
    if (target === null || amountCents <= 0) return;
    setError(null);
    try {
      await withdraw.mutateAsync({ goalId: target.id, toAccountId: accountId, amountCents });
      setDone({ name: target.name, amountCents });
      setTarget(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Virement impossible.');
    }
  }

  return (
    <BottomSheet anchorRef={anchorRef} label="Alimenter le compte courant" onClose={onClose}>
      <p className="mt-1 text-xs text-ink-secondary">
        Choisissez un pôle pour couvrir le découvert. L’argent est vraiment déplacé, et le virement
        apparaît dans vos écritures.
      </p>

      {done !== null && (
        <p role="status" className="mt-3 text-sm text-success-text">
          {formatEUR(done.amountCents)} virés depuis {done.name}.
        </p>
      )}

      {goals.length === 0 ? (
        <p className="mt-4 text-sm text-ink-secondary">
          Aucun pôle d’épargne disponible pour renflouer ce compte.
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {goals.map((goal) => (
            <li key={goal.id} className="rounded-xl border border-line p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm">{goal.name}</p>
                  <p className="tabular text-xs text-ink-muted">
                    {formatEUR(goal.savedCents)} disponibles
                  </p>
                </div>
                {target?.id !== goal.id && (
                  <button
                    type="button"
                    onClick={() => openFor(goal)}
                    className="shrink-0 rounded-lg border border-line px-3 py-1.5 text-xs text-ink-secondary"
                  >
                    Virer sur le compte courant
                  </button>
                )}
              </div>

              {target?.id === goal.id && (
                <div className="mt-3 border-t border-line pt-3">
                  <AmountField
                    label={`Montant à virer depuis ${goal.name}`}
                    valueCents={amountCents}
                    onChange={setAmountCents}
                    hint={`Disponible : ${formatEUR(goal.savedCents)}`}
                  />
                  {amountCents > goal.savedCents && (
                    <p className="mt-2 text-xs text-critical">
                      Le pôle ne contient que {formatEUR(goal.savedCents)}.
                    </p>
                  )}
                  {error !== null && (
                    <p role="alert" className="mt-2 text-sm text-critical">
                      {error}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTarget(null)}
                      className="flex-1 rounded-xl border border-line py-2.5 text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      disabled={
                        withdraw.isPending || amountCents <= 0 || amountCents > goal.savedCents
                      }
                      onClick={() => void submit()}
                      className="flex-1 rounded-xl bg-series py-2.5 text-sm font-medium text-white disabled:opacity-50"
                    >
                      Virer
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </BottomSheet>
  );
}
